import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  goTo,
} from "@/store/navigationSlice";
import { setAgentSlug, setSelectedAgent, fetchAgentsOnce } from "@/store/agentsSlice";
import { setConversationId, fetchAllConversations } from "@/store/conversationsSlice";
import { PAGES, PageType } from "@/app/navigation/navigation.constant";
import { MainLayout } from "@/components/layout/main/MainLayout";
import { store } from "./store";

// ----------------------
// Redux → URL
// ----------------------
const pageToPath = (page: PageType) => {
  switch (page) {
    case PAGES.AGENT_DETAILS: {
      const state = store.getState();
      const agentSlug = state.agents.agentSlug;
      const conversationId = state.conversations.conversationId;
      return conversationId
        ? `/agent/${agentSlug}/${conversationId}`
        : `/agent/${agentSlug}`;
    }
    case PAGES.HOME:
      return "/home";
    case PAGES.NOTIFICATIONS:
      return "/notifications";
    case PAGES.SAVED:
      return "/saved";
    case PAGES.RESOURCES:
      return "/resources";
    case PAGES.ADMIN:
      return "/admin";
    case PAGES.AGENT_CATALOG:
      return "/admin/agent-catalog";
    case PAGES.API_DOCUMENTATION:
      return "/admin/api-docs";
    case PAGES.DEVKIT:
      return "/devkit";
    case PAGES.AGENTS:
      return "/agents";
    case PAGES.CONVERSATIONS:
      return "/conversations";
    case PAGES.CARDS_MOCKUP:
      return "/cards-mockup";
    default:
      return "/agents";
  }
};

// ----------------------
// URL → Redux
// ----------------------
const pathToPage = (path: string): PageType => {
  if (path.startsWith("/agent/")) return PAGES.AGENT_DETAILS;

  switch (path) {
    case "/":
    case "/agents":
      return PAGES.AGENTS;
    case "/home":
      return PAGES.HOME;
    case "/notifications":
      return PAGES.NOTIFICATIONS;
    case "/saved":
      return PAGES.SAVED;
    case "/resources":
      return PAGES.RESOURCES;
    case "/admin":
      return PAGES.ADMIN;
    case "/admin/agent-catalog":
      return PAGES.AGENT_CATALOG;
    case "/admin/api-docs":
      return PAGES.API_DOCUMENTATION;
    case "/devkit":
      return PAGES.DEVKIT;
    case "/conversations":
      return PAGES.CONVERSATIONS;
    case "/cards-mockup":
      return PAGES.CARDS_MOCKUP;
    default:
      return PAGES.AGENTS;
  }
};

export default function App() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const activePage = useAppSelector((s) => s.navigation.activePage);

  const [hydrated, setHydrated] = useState(false);

  // Direction locks
  const isUrlDriving = useRef(false);
  const isReduxDriving = useRef(false);

  const parseAgentPath = (path: string) => {
    const match = path.match(/^\/agent\/([^\/]+)(?:\/(.+))?$/);
    if (!match) return null;

    return {
      agentId: match[1],
      conversationId: match[2] || null,
    };
  };

  // ----------------------
  // URL → REDUX
  // ----------------------
  useEffect(() => {
    if (isReduxDriving.current) return;

    isUrlDriving.current = true;

    const page = pathToPage(location.pathname);

    if (page !== activePage) {
      dispatch(goTo(page));
    }

    if (page === PAGES.AGENT_DETAILS) {
      const parsed = parseAgentPath(location.pathname);
      if (parsed) {
        const { agentId, conversationId } = parsed;

        dispatch(setAgentSlug(agentId));
        dispatch(setConversationId(conversationId));
      }
    }

    // Check if data needs to be loaded for direct navigation
    if (page === PAGES.AGENTS) {
      const { agentsLoaded } = store.getState().agents;
      if (!agentsLoaded) {
        dispatch(fetchAgentsOnce());
      }
    }

    // 🔥 Hydration must ALWAYS happen
    setHydrated(true);
    isUrlDriving.current = false;
  }, [location.pathname]);

  // ----------------------
  // REDUX → URL
  // ----------------------
  useEffect(() => {
    if (!hydrated) return;
    if (isUrlDriving.current) return;

    const target = pageToPath(activePage);

    if (location.pathname !== target) {
      isReduxDriving.current = true;
      navigate(target, { replace: true });
      isReduxDriving.current = false;
    }
  }, [activePage, hydrated]);

  return <MainLayout />;
}
